<template>
  <div>
    <div fluid class="px-4 pt-2">
      <v-card :color="getThemeColor" dark class="mb-4 elevation-6">
        <v-card-title
          class="text-h4 font-weight-bold d-flex justify-space-between align-center"
        >
          <v-icon large left>mdi-account-group</v-icon>
          {{ getClientLabel }} Management
        </v-card-title>
        <v-card-subtitle
          >Manage all your {{ getClientLabel.toLowerCase() }} profiles, billing,
          and communication.</v-card-subtitle
        >
      </v-card>

      <v-row>
        <!-- v-show="!selectedClient && !isCreating" class="client-list-col" -->
        <v-col :cols="isMobile ? 12 : 3">
          <v-card :dark="isDarkTheme" class="pa-2 elevation-4">
            <v-card-title class="d-flex justify-space-between align-center">
              {{ getClientLabel }} List
              <v-btn :color="getThemeColor" small dark @click="startCreateNew">
                <v-icon left>mdi-plus</v-icon> Add New
              </v-btn>
            </v-card-title>
            <v-text-field
              v-model="search"
              :label="`Search ${getClientLabel}`"
              prepend-inner-icon="mdi-magnify"
              single-line
              hide-details
              filled
              dense
              class="ma-3"
            ></v-text-field>
            <v-data-table
              :headers="listHeaders"
              :items="clients"
              :search="search"
              @click:row="selectClient"
              item-key="id"
              class="elevation-1"
            >
              <template v-slot:item.name="{ item }">
                <v-list-item dense class="pa-0">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      Ref #:
                      {{ item.client_reference || "" }}</v-list-item-subtitle
                    >
                    <v-list-item-subtitle>
                      {{ item.institution || "" }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click.stop="selectClient(item)">
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <!-- :md="selectedClient || isCreating ? 8 : 12" -->
        <v-col :cols="isMobile ? 12 : 9" class="client-profile-col">
          <v-card
            v-if="!selectedClient && !isCreating"
            :dark="isDarkTheme"
            class="pa-6 text-center elevation-4"
          >
            <v-icon size="64" :color="getThemeColor">mdi-account-check</v-icon>
            <p class="mt-4 text-h6">
              Click on a {{ getClientLabel.toLowerCase() }} from the list to
              Select or click 'Add New' to create one.
            </p>
          </v-card>

          <v-card v-else :dark="isDarkTheme" class="pa-2 elevation-4">
            <v-toolbar flat :color="getThemeColor" dark>
              <v-toolbar-title>{{
                isCreating ? "New " + getClientLabel : localClient.name
              }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                v-if="!isCreating"
                icon
                @click="deleteConfirmDialog = true"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn icon @click="cancelAction">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-tabs v-model="activeTab" :color="getThemeColor" grow>
              <v-tab>Profile & Edit</v-tab>
              <v-tab>Billing & Financial</v-tab>
              <v-tab>Communication & Notes</v-tab>
              <v-tab v-if="clientIsMedicalLegal">Case Management</v-tab>
              <v-tab v-if="clientIsMechanics">Vehicles</v-tab>
              <v-tab>Insurance</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <v-card-text>
                  <v-form ref="profileForm" v-model="validForm">
                    <v-row>
                      <v-col cols="12" sm="3" class="text-center">
                        <v-avatar
                          size="128"
                          color="grey lighten-3"
                          class="mb-3"
                        >
                          <img
                            v-if="localClient.image"
                            :src="localClient.image"
                            alt="Client Image"
                          />
                          <v-icon v-else size="80">mdi-account</v-icon>
                        </v-avatar>
                        <v-file-input
                          label="Upload Image"
                          dense
                          outlined
                          prepend-icon="mdi-camera"
                          @change="onFileChange"
                          accept="image/*"
                        ></v-file-input>
                      </v-col>
                      <v-col cols="12" sm="9">
                        <v-text-field
                          v-model="localClient.name"
                          :label="`${getClientLabel} Full Names`"
                          outlined
                          required
                        ></v-text-field>
                        <v-text-field
                          v-model="localClient.client_reference"
                          label="Client Reference"
                          outlined
                          required
                        ></v-text-field>
                        <v-text-field
                          v-model="localClient.id_number"
                          label="Client ID Number / Business Registration Number"
                          outlined
                        ></v-text-field>
                        <v-text-field
                          v-model="localClient.email"
                          label="Email Address"
                          outlined
                          type="email"
                        ></v-text-field>
                        <v-text-field
                          v-model="localClient.phone"
                          label="Primary Phone"
                          outlined
                        ></v-text-field>
                        <v-text-field
                          v-model="localClient.address"
                          label="Primary Address"
                          outlined
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-divider class="my-3"></v-divider>
                    <h4 class="text-subtitle-1">Additional Details</h4>
                    <v-row>
                      <v-col cols="12" sm="4"
                        ><v-text-field
                          v-model="localClient.dob"
                          label="Date of Birth (DOB)"
                          outlined
                          placeholder="YYYY-MM-DD"
                        ></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="4"
                        ><v-select
                          v-model="localClient.gender"
                          :items="['Male', 'Female', 'Other']"
                          label="Gender"
                          outlined
                        ></v-select
                      ></v-col>
                      <v-col cols="12" sm="4"
                        ><v-text-field
                          v-model="localClient.alt_contact"
                          label="Alternative Contact"
                          outlined
                        ></v-text-field
                      ></v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6"
                        ><v-text-field
                          v-model="localClient.institution"
                          label="Institution / Employer / Business Name"
                          outlined
                        ></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6"
                        ><v-text-field
                          v-model="localClient.position"
                          label="Position/Title"
                          outlined
                        ></v-text-field
                      ></v-col>
                    </v-row>
                  </v-form>
                </v-card-text>
                <v-card-actions class="d-flex justify-end">
                  <v-btn
                    :color="getThemeColor"
                    :disabled="!validForm"
                    dark
                    @click="handleSaveClient"
                    >{{ isCreating ? "Create Client" : "Save Changes" }}</v-btn
                  >
                </v-card-actions>
              </v-tab-item>

              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-3">Financial Summary</h3>
                  <v-row>
                    <v-col
                      ><v-card outlined class="pa-2 text-center"
                        ><div class="text-caption">Total Invoiced</div>
                        <div class="text-h6 error--text">
                          {{ formatCurrency(billingSummary.totalInvoiced) }}
                        </div></v-card
                      ></v-col
                    >
                    <v-col
                      ><v-card outlined class="pa-2 text-center"
                        ><div class="text-caption">Total Paid</div>
                        <div class="text-h6 success--text">
                          {{ formatCurrency(billingSummary.totalPaid) }}
                        </div></v-card
                      ></v-col
                    >
                    <v-col
                      ><v-card outlined class="pa-2 text-center"
                        ><div class="text-caption">Balance Outstanding</div>
                        <div class="text-h6 primary--text">
                          {{
                            formatCurrency(billingSummary.balanceOutstanding)
                          }}
                        </div></v-card
                      ></v-col
                    >
                  </v-row>

                  <v-divider class="my-4"></v-divider>

                  <h3 class="mb-3">
                    Invoices (Total: {{ sampleInvoices.length }})
                  </h3>
                  <v-data-table
                    :headers="invoiceHeaders"
                    :items="sampleInvoices"
                    class="elevation-1 mb-4"
                  ></v-data-table>

                  <h3 class="mb-3">
                    Payments Made (Total: {{ samplePayments.length }})
                  </h3>
                  <v-data-table
                    :headers="paymentHeaders"
                    :items="samplePayments"
                    class="elevation-1"
                  ></v-data-table>

                  <v-divider class="my-4"></v-divider>

                  <h3 class="mb-3">Next Appointment/Appearance</h3>
                  <v-card outlined class="pa-3">
                    <p class="text-h6 mb-0">
                      {{ nextAppointment.date }} at {{ nextAppointment.time }}
                    </p>
                    <p class="mb-0 text-caption">
                      {{ nextAppointment.reason }}
                    </p>
                  </v-card>
                </v-card-text>
              </v-tab-item>

              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-3">Client Notes</h3>
                  <v-textarea
                    v-model="localClient.notes"
                    label="Add/Edit Client Notes"
                    outlined
                  ></v-textarea>
                  <v-divider class="my-4"></v-divider>

                  <h3 class="mb-3">Communication Log (SMS, Tickets, Emails)</h3>
                  <v-timeline dense clipped>
                    <v-timeline-item
                      v-for="comm in sampleComms"
                      :key="comm.id"
                      small
                      :color="getCommColor(comm.type)"
                    >
                      <v-row dense class="pt-1">
                        <v-col cols="3"
                          ><strong>{{ comm.date }}</strong></v-col
                        >
                        <v-col cols="9">
                          <v-chip
                            label
                            small
                            :color="getCommColor(comm.type)"
                            dark
                            class="mr-2"
                            >{{ comm.type }}</v-chip
                          >
                          <span class="font-weight-light">{{
                            comm.summary
                          }}</span>
                        </v-col>
                      </v-row>
                    </v-timeline-item>
                  </v-timeline>
                </v-card-text>
              </v-tab-item>

              <v-tab-item v-if="clientIsMedicalLegal">
                <v-card-text>
                  <h3 class="mb-3">
                    Active Cases/Files (Total: {{ sampleCases.length }})
                  </h3>
                  <v-data-table
                    :headers="caseHeaders"
                    :items="sampleCases"
                    class="elevation-1"
                  ></v-data-table>
                </v-card-text>
              </v-tab-item>

              <v-tab-item v-if="clientIsMechanics">
                <v-card-text>
                  <h3 class="mb-3">
                    Registered Vehicles (Total: {{ sampleVehicles.length }})
                  </h3>
                  <v-data-table
                    :headers="vehicleHeaders"
                    :items="sampleVehicles"
                    class="elevation-1"
                  ></v-data-table>
                </v-card-text>
              </v-tab-item>

              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-3">
                    Known Insurance Schemes/Companies (Total:
                    {{ sampleInsurance.length }})
                  </h3>
                  <v-data-table
                    :headers="insuranceHeaders"
                    :items="sampleInsurance"
                    class="elevation-1"
                  ></v-data-table>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>

            <v-dialog v-model="deleteConfirmDialog" max-width="500">
              <v-card>
                <v-card-title class="headline error white--text"
                  >Confirm Deletion</v-card-title
                >
                <v-card-text class="pt-4">
                  Are you sure you want to permanently delete **{{
                    localClient.name
                  }}**? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue" text @click="deleteConfirmDialog = false"
                    >Cancel</v-btn
                  >
                  <v-btn color="error" text @click="confirmDelete"
                    >Delete</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card>
        </v-col>
      </v-row>

      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="3000"
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false"
            >Close</v-btn
          >
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";
export default {
  data: () => ({
    clientsTable: "clients",
    // UI State
    selectedClient: null,
    localClient: {}, // Working copy for editing
    isCreating: false,
    search: "",
    activeTab: 0,
    validForm: true,
    deleteConfirmDialog: false,

    // Sample Data
    clients: [
      {
        id: 1,
        name: "John Doe",
        client_reference: "JD-001",
        id_number: "7801015000085",
        institution: "Acme Corp",
        position: "Manager",
        dob: "1978-01-01",
        gender: "Male",
        email: "john.doe@example.com",
        alt_contact: null,
        phone: "0831234567",
        address: "15 Main Road",
        city: "Vryheid",
        postal_code: "3100",
        province: "KZN",
        notes: "Very reliable customer.",
        emis: null,
        image: null,
      },
      {
        id: 2,
        name: "Pioneer School",
        client_reference: "PS-045",
        id_number: "0000000000000",
        institution: null,
        position: null,
        dob: null,
        gender: null,
        email: "admin@pioneer.school",
        alt_contact: "0319876543",
        phone: "0331112222",
        address: "4 School Lane",
        city: "Pietermaritzburg",
        postal_code: "3200",
        province: "KZN",
        notes: "School for annual maintenance contract.",
        emis: null,
        image: null,
      },
      {
        id: 3,
        name: "Sipho Zulu Attorneys",
        client_reference: "SZ-202",
        id_number: "2019/123456/07",
        institution: null,
        position: null,
        dob: null,
        gender: null,
        email: "sipho@szattorneys.co.za",
        alt_contact: null,
        phone: "0115551234",
        address: "100 Sandton Drive",
        city: "Johannesburg",
        postal_code: "2000",
        province: "Gauteng",
        notes: null,
        emis: null,
        image: null,
      },
    ],

    // Detail View Sample Data (should be fetched dynamically based on selectedClient.id)
    sampleInvoices: [
      {
        id: 1001,
        date: "2023-11-01",
        description: "Monthly Retainer Fee",
        amount: 5000.0,
        status: "Outstanding",
      },
      {
        id: 1002,
        date: "2023-10-15",
        description: "Consultation & Filing",
        amount: 2500.0,
        status: "Paid",
      },
      {
        id: 1003,
        date: "2023-09-05",
        description: "Service Call Out",
        amount: 750.0,
        status: "Outstanding",
      },
    ],
    samplePayments: [
      {
        id: 501,
        date: "2023-10-20",
        method: "EFT",
        amount: 2500.0,
        invoice_id: 1002,
      },
      {
        id: 502,
        date: "2023-11-05",
        method: "Card",
        amount: 1000.0,
        invoice_id: 1001,
      },
    ],
    sampleComms: [
      {
        id: 1,
        date: "2023-12-10",
        type: "SMS",
        summary: "Invoice 1001 reminder sent.",
        details: "",
      },
      {
        id: 2,
        date: "2023-12-05",
        type: "Email",
        summary: "Sent case update report.",
        details: "",
      },
    ],
    sampleCases: [
      {
        id: "LEG-2023-001",
        file_number: "23/JHB/0045",
        type: "Civil Litigation",
        status: "Active",
        opened: "2023-05-10",
      },
    ],
    sampleVehicles: [
      {
        id: 1,
        make: "Toyota",
        model: "Corolla",
        year: 2018,
        VIN: "JTMG1234567890",
        mileage: "85,000 km",
      },
    ],
    sampleInsurance: [
      {
        id: 1,
        company: "Discovery Health",
        scheme: "Executive Plan",
        policy: "DH0012345",
        status: "Active",
      },
    ],
    nextAppointment: {
      date: "2023-12-24",
      time: "11:00 AM",
      reason: "Review of outstanding account and service plan.",
    },

    // Snackbar for feedback
    snackbar: { show: false, message: "", color: "" },
  }),

  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getCompanyLabels() {
      return this.$store.getters.getCompanyLabels;
    },
    getClientLabel() {
      return this.$store.getters.getCompanyLabels.client;
    },
    isDarkTheme() {
      return false;
    },
    listHeaders() {
      return [
        { text: "Name/ID", value: "name" },
        { text: "Phone", value: "phone", align: "end", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ];
    },
    invoiceHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Date", value: "date" },
        { text: "Amount", value: "amount", align: "end" },
        { text: "Status", value: "status", align: "center" },
      ];
    },
    paymentHeaders() {
      return [
        { text: "Date", value: "date" },
        { text: "Amount", value: "amount", align: "end" },
        { text: "Method", value: "method" },
      ];
    },
    caseHeaders() {
      return [
        { text: "Case/File No.", value: "file_number" },
        { text: "Type", value: "type" },
        { text: "Status", value: "status" },
      ];
    },
    vehicleHeaders() {
      return [
        { text: "Make", value: "make" },
        { text: "Model", value: "model" },
        { text: "VIN", value: "VIN" },
      ];
    },
    insuranceHeaders() {
      return [
        { text: "Company", value: "company" },
        { text: "Scheme", value: "scheme" },
        { text: "Policy No.", value: "policy" },
      ];
    },
    // 8. Clients outstanding total invoices amount, total paid, and balance outstanding.
    billingSummary() {
      const totalInvoiced = this.sampleInvoices.reduce(
        (sum, inv) => sum + inv.amount,
        0
      );
      const totalPaid = this.samplePayments.reduce(
        (sum, pay) => sum + pay.amount,
        0
      );
      const balanceOutstanding = totalInvoiced - totalPaid;

      return {
        totalInvoiced,
        totalPaid,
        balanceOutstanding: balanceOutstanding > 0 ? balanceOutstanding : 0,
      };
    },
    clientIsMedicalLegal() {
      return ["Patient", "Client"].includes(this.getClientLabel);
    },
    clientIsMechanics() {
      return this.getClientLabel === "Customer";
    },
  },

  watch: {
    // Sync the local editing copy when a client is selected
    selectedClient(newVal) {
      if (newVal) {
        // Deep clone the object to prevent mutation of the main clients list
        this.localClient = JSON.parse(JSON.stringify(newVal));
        this.activeTab = 0; // Reset to profile tab
      } else {
        // Clear local client if none is selected
        this.localClient = this.getNewClientModel();
      }
    },
    // Set local client model when starting a new creation
    isCreating(newVal) {
      if (newVal) {
        this.localClient = this.getNewClientModel();
        this.activeTab = 0;
      }
    },
  },
  mounted() {
    this.getAllClients();
  },
  methods: {
    //Done
    getNewClientModel() {
      return {
        id: null,
        name: "",
        client_reference: "",
        id_number: "0000000000000",
        institution: null,
        position: null,
        dob: null,
        gender: null,
        email: null,
        alt_contact: null,
        phone: null,
        address: "123 Reinhold Street",
        city: "Vryheid",
        postal_code: "3100",
        province: null,
        notes: null,
        emis: null,
        image: null, // Base64 image data
      };
    },
    //Done
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-ZA", {
        style: "currency",
        currency: "ZAR",
      }).format(amount);
    },
    //Done
    getCommColor(type) {
      switch (type) {
        case "SMS":
          return "blue";
        case "Email":
          return "orange";
        case "Ticket":
          return "purple";
        default:
          return "grey";
      }
    },
    // 5. Client image upload with base64
    onFileChange(file) {
      if (!file) {
        this.localClient.image = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        // Base64 string for image upload
        this.localClient.image = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    //Done
    selectClient(client) {
      this.isCreating = false;
      this.selectedClient = client;
    },

    //Done
    startCreateNew() {
      this.selectedClient = null;
      this.isCreating = true;
    },

    //Done
    cancelAction() {
      this.selectedClient = null;
      this.isCreating = false;
    },

    //Done
    async handleSaveClient() {
      if (!this.$refs.profileForm.validate()) return;

      const client = this.localClient;
      const isNew = this.isCreating;

      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/save-or-update-client",
          { client },
          this.getAccessToken
        )
        .then((response) => {
          this.$store.commit("setLoader", false);

          if (response && response.status == "success") {
            // **SUCCESS SIMULATION**
            if (isNew) {
              this.clients.push(response.client);
              this.selectedClient = response.client;
              this.isCreating = false;
              this.$toast.success(
                `${this.getClientLabel} **${response.client.name}** created successfully!`,
                { duration: 5000 }
              );
            } else {
              // Find and replace the client in the list with the local changes
              const index = this.clients.findIndex((c) => c.id === client.id);
              if (index !== -1) {
                this.$set(this.clients, index, response.client);
                this.selectedClient = response.client;
              }
              this.$toast.success(
                `${this.getClientLabel} **${response.client.name}** updated successfully!`,
                { duration: 5000 }
              );
            }
          } else {
            this.$toast.error(
              "Oops, Something Went Wrong. Consult System Admin"
            );
          }
        });
    },

    //Done
    async confirmDelete() {
      this.deleteConfirmDialog = false;
      const clientId = this.localClient.id;
      const clientName = this.localClient.name;
      this.$store.commit("setLoader", true);
      await helpersJs.deleteSingleRow(this.clientsTable, clientId);
      this.$store.commit("setLoader", false);

      // **SUCCESS SIMULATION**
      this.clients = this.clients.filter((c) => c.id !== clientId);
      this.selectedClient = null;
      this.isCreating = false;
      this.$toast.success(
        `${this.getClientLabel} **${clientName}** deleted successfully.`,
        { duration: 5000 }
      );
    },
    //Done
    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    //Done
    getAllClients() {
      this.$store.commit("setLoader", true);
      axios
        .getData("/auth/get-all-clients", this.getAccessToken)
        .then((clients) => {
          this.clients = clients;
          this.$store.commit("setLoader", false);
        });
    },
  },
};
</script>

<style scoped>
/* Master-Detail Columns on desktop */
@media (min-width: 960px) {
  .client-list-col {
    max-width: 350px;
    flex: 0 0 350px;
    transition: all 0.3s;
  }
  .client-profile-col {
    flex-grow: 1;
    transition: all 0.3s;
  }
}
/* Ensure the detail view takes full width on mobile/when list is hidden */
@media (max-width: 959px) {
  .client-profile-col {
    width: 100%;
  }
}
</style>
